// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  src/features/operacional/hooks/useCondicional.ts
// ════════════════════════════════════════════════════════════════════════════

import { keepPreviousData, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { FiltroConsultaOperacional } from '../consultas';
import { condicionalService } from '../services/condicionalService';
import type { ListaFiltros } from '../services/listaFiltros';
import { toast } from 'sonner';
import { getApiErrorMessage } from '@/lib/apiClient';
import type { CriarCondicionalRequest, AtualizarCondicionalRequest, RegistrarDevolucaoRequest } from '../types';

interface UseCondicionalParams {
  pagina?: number;
  tamanhoPagina?: number;
  /** Busca, status, período, filtros extras e ordem — aplicados no servidor. */
  filtros?: FiltroConsultaOperacional | ListaFiltros;
}

export const useCondicional = ({ pagina = 1, tamanhoPagina = 20, filtros }: UseCondicionalParams = {}) => {
  const queryClient = useQueryClient();

  const { data: paginado, isLoading, isFetching } = useQuery({
    queryKey: ['condicional', pagina, tamanhoPagina, filtros],
    queryFn: () => condicionalService.getAll(pagina, tamanhoPagina, filtros),
    placeholderData: keepPreviousData, // a tabela não pisca a cada letra da busca
  });

  // KPIs da consulta inteira (o status não entra: os cards mostram a divisão por status).
  const resumo = useQuery({
    queryKey: ['condicional', 'resumo', filtros],
    queryFn: () => condicionalService.getResumo(filtros as FiltroConsultaOperacional),
    placeholderData: keepPreviousData,
  });

  const condicionais  = paginado?.itens       ?? [];
  const total         = paginado?.total        ?? 0;
  const totalPaginas  = paginado?.totalPaginas ?? 1;
  const resumoLista = paginado?.resumo; // KPIs v118 (vêm na listagem)

  // Condicionais vencidas (validade no passado, ainda abertas) — para o alerta de reconciliação.
  const { data: vencidas = [] } = useQuery({
    queryKey: ['condicional', 'vencidas'],
    queryFn: () => condicionalService.getVencidas(),
  });

  const expirarMutation = useMutation({
    mutationFn: () => condicionalService.expirarVencidas(),
    onSuccess: (r) => {
      queryClient.invalidateQueries({ queryKey: ['condicional'] });
      if (!r.habilitado) {
        toast.info('Expiração automática desativada nas configurações. Faça o acerto manual das vencidas.');
      } else if (r.processadas === 0) {
        toast.info('Nenhuma condicional vencida a expirar.');
      } else {
        toast.success(`${r.processadas} condicional(is) vencida(s) expirada(s); saldo devolvido ao depósito.`);
      }
    },
    onError: (error) => toast.error(getApiErrorMessage(error)),
  });

  const createMutation = useMutation({
    mutationFn: (request: CriarCondicionalRequest) => condicionalService.create(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['condicional'] });
      toast.success('Venda Condicional criada com sucesso');
    },
    onError: (error) => toast.error(getApiErrorMessage(error)),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: AtualizarCondicionalRequest }) =>
      condicionalService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['condicional'] });
      toast.success('Condicional atualizada com sucesso');
    },
    onError: (error) => toast.error(getApiErrorMessage(error)),
  });

  const devolucaoMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: RegistrarDevolucaoRequest }) =>
      condicionalService.registrarDevolucao(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['condicional'] });
      toast.success('Devolução registrada com sucesso');
    },
    onError: (error) => toast.error(getApiErrorMessage(error)),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => condicionalService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['condicional'] });
      toast.success('Condicional excluída com sucesso');
    },
    onError: (error) => toast.error(getApiErrorMessage(error)),
  });

  return {
    resumo: resumo.data,
    condicionais,
    total,
    totalPaginas,
    resumoLista,
    vencidas,
    isLoading,
    isFetching,
    createCondicional: createMutation.mutate,
    updateCondicional: updateMutation.mutate,
    registrarDevolucao: devolucaoMutation.mutate,
    deleteCondicional: deleteMutation.mutate,
    expirarVencidas: expirarMutation.mutate,
    isExpirando: expirarMutation.isPending,
    isCreating: createMutation.isPending,
  };
};
